import { useSnackbar } from 'contexts/snackbar';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getSubmissionCreatedDraftNumber } from 'utils/chat';
import { ChatRole, ChatSystemType } from 'utils/chat/chatConstants';
import { getChatSystemMessageText, getDateKey, getMessageText } from 'utils/chat/chatHelpers';

const groupMessagesByDate = (messages = []) => {
    return messages.reduce((groups, message) => {
        const createdAt = message.createdAt || new Date();
        const dateKey = getDateKey(createdAt);
        const currentGroup = groups[groups.length - 1];

        if (currentGroup?.dateKey === dateKey) {
            currentGroup.items.push(message);
            return groups;
        }

        return [...groups, { dateKey, date: createdAt, items: [message] }];
    }, []);
};

const dedupeSubmissionCreatedMessages = (messages = []) => {
    const seenSubmissionCreatedDraftNumbers = new Set();

    return messages.filter((message) => {
        const rawMessage = {
            role: message.role,
            message_text: getMessageText(message.content || []),
            system_type: message.metadata?.custom?.system_type,
            system_data: message.metadata?.custom?.system_data,
        };
        const draftNumber = getSubmissionCreatedDraftNumber(rawMessage);

        if (draftNumber === null) return true;
        if (seenSubmissionCreatedDraftNumbers.has(draftNumber)) return false;

        seenSubmissionCreatedDraftNumbers.add(draftNumber);
        return true;
    });
};

const sortMessagesByCreatedAt = (messages = []) => {
    return [...messages].sort((left, right) => {
        const leftTime = new Date(left.createdAt || 0).getTime();
        const rightTime = new Date(right.createdAt || 0).getTime();

        return leftTime - rightTime;
    });
};

const getSystemMessagePresentation = ({ message, t }) => {
    const systemType = message.metadata?.custom?.system_type;
    const messageText = getMessageText(message.content || []);
    const draftNumber = getSubmissionCreatedDraftNumber(message);
    const isSubmissionCreated = systemType === ChatSystemType.SUBMISSION_CREATED || draftNumber !== null;
    const timeLabel = t('ui.format-time-only', { val: message.createdAt || new Date() });
    const text = isSubmissionCreated
        ? messageText || `${draftNumber || ''} ${timeLabel}`.trim()
        : getChatSystemMessageText({
              fallbackText: messageText,
              includeTimestamp: true,
              message,
              t,
              timeLabel,
          });

    if (!text.trim()) return null;

    return {
        draftNumber: isSubmissionCreated ? draftNumber : null,
        text,
        timeLabel,
    };
};

const buildTimeline = ({ isRunning, messages, t }) => {
    const normalizedMessages = sortMessagesByCreatedAt(dedupeSubmissionCreatedMessages(messages));
    const lastMessageId = normalizedMessages[normalizedMessages.length - 1]?.id;
    const messageIndexById = messages.reduce((indexes, message, index) => {
        indexes[message.id] = index;
        return indexes;
    }, {});

    return groupMessagesByDate(normalizedMessages).map((group) => {
        return {
            ...group,
            items: group.items
                .map((message) => {
                    const createdAt = message.createdAt || new Date();

                    if (message.role === ChatRole.SYSTEM) {
                        const presentation = getSystemMessagePresentation({ message, t });
                        if (!presentation) return null;

                        return {
                            draftNumber: presentation.draftNumber,
                            id: message.id,
                            role: ChatRole.SYSTEM,
                            text: presentation.text,
                            timeLabel: presentation.timeLabel,
                        };
                    }

                    const text = getMessageText(message.content || []);
                    const hasText = Boolean(text.trim());
                    const isStreamingAssistantMessage =
                        isRunning && message.role === ChatRole.ASSISTANT && message.id === lastMessageId;
                    const shouldShowMessageMeta =
                        message.role === ChatRole.ASSISTANT ? hasText && !isStreamingAssistantMessage : true;

                    return {
                        id: message.id,
                        isStreamingAssistantMessage,
                        role: message.role,
                        message,
                        messageIndex: messageIndexById[message.id] ?? -1,
                        shouldShowMessageMeta,
                        text,
                        timeLabel: t('ui.format-time-only', { val: createdAt }),
                    };
                })
                .filter(Boolean),
        };
    });
};

/**
 * Presentation view model for the transcript message list.
 *
 * Use from `ChatMessages` to convert assistant-ui messages into grouped timeline rows,
 * system-message labels, streaming metadata, timestamps, and copy-to-clipboard behavior.
 * It does not fetch, mutate, or send chat data.
 *
 * @param {Object} props
 * @param {boolean} props.isRunning Whether the assistant runtime is currently responding.
 * @param {Array<Object>} props.messages Assistant-ui thread messages.
 * @returns {{contentRef: Object, copyAssistantMessage: Function, timeline: Array<Object>}} Render and copy model.
 */
const useChatMessagesViewModel = ({ isRunning, messages }) => {
    const { t } = useTranslation();
    const { message } = useSnackbar();
    const contentRef = useRef(null);

    const timeline = useMemo(() => {
        return buildTimeline({
            isRunning,
            messages,
            t,
        });
    }, [isRunning, messages, t]);

    const copyAssistantMessage = useCallback(
        async ({ fallbackText, messageId }) => {
            try {
                const messageElement = [
                    ...(contentRef.current?.querySelectorAll('[data-message-content-id]') || []),
                ].find((element) => element.dataset.messageContentId === messageId);

                if (messageElement && navigator.clipboard.write && typeof ClipboardItem !== 'undefined') {
                    await navigator.clipboard.write([
                        new ClipboardItem({
                            'text/html': new Blob([messageElement.innerHTML], { type: 'text/html' }),
                            'text/plain': new Blob([messageElement.innerText], { type: 'text/plain' }),
                        }),
                    ]);
                } else {
                    await navigator.clipboard.writeText(fallbackText);
                }

                message.success(t('message.copied-to-clipboard', 'Copied to clipboard.'));
            } catch (error) {
                console.error(error);
            }
        },
        [message, t]
    );

    return {
        contentRef,
        copyAssistantMessage,
        timeline,
    };
};

export default useChatMessagesViewModel;
